import { useState, useEffect, useCallback } from 'react'
import {
  IExpensesByType,
  IExpenseItem,
  ICreateDailyExpenseInput,
  IUpdateDailyExpenseInput
} from '@/supabase/DailySalesSummary/dailySalesSummary.interface'
import {
  getExpensesByDate,
  createExpense,
  updateExpense,
  deleteExpense
} from '@/supabase/DailySalesSummary/dailySalesSummary'

interface UseExpenseManagementReturn {
  // Data
  expenses: IExpensesByType
  
  // Loading states
  loading: boolean
  updating: boolean
  
  // Error state
  error: string | null
  
  // Actions
  refreshExpenses: () => Promise<void>
  addExpense: (expenseType: 'FUEL' | 'EMPLOYEE_OVERTIME' | 'OTHER', name: string, amount: number) => Promise<boolean>
  editExpense: (expenseId: string, name: string, amount: number) => Promise<boolean>
  removeExpense: (expenseId: string) => Promise<boolean>
  
  // Totals
  getTotalByType: (expenseType: 'FUEL' | 'EMPLOYEE_OVERTIME' | 'OTHER') => number
  getGrandTotal: () => number
}

export const useExpenseManagement = (selectedDate: string): UseExpenseManagementReturn => {
  // Data state
  const [expenses, setExpenses] = useState<IExpensesByType>({
    FUEL: [],
    EMPLOYEE_OVERTIME: [],
    OTHER: []
  })
  
  // Loading and error states
  const [loading, setLoading] = useState(false)
  const [updating, setUpdating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load expenses for selected date
  const loadExpenses = useCallback(async () => {
    if (!selectedDate) return

    setLoading(true)
    setError(null)

    try {
      const expensesData = await getExpensesByDate(selectedDate)
      
      if (expensesData) {
        setExpenses(expensesData)
      } else {
        // Initialize empty expenses if no data found
        setExpenses({
          FUEL: [],
          EMPLOYEE_OVERTIME: [],
          OTHER: []
        })
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'เกิดข้อผิดพลาดในการโหลดข้อมูลค่าใช้จ่าย')
      console.error('Error loading expenses:', err)
    } finally {
      setLoading(false)
    }
  }, [selectedDate])

  // Refresh expenses
  const refreshExpenses = useCallback(async () => {
    await loadExpenses()
  }, [loadExpenses])

  // Add expense
  const addExpense = useCallback(async (
    expenseType: 'FUEL' | 'EMPLOYEE_OVERTIME' | 'OTHER',
    name: string,
    amount: number
  ): Promise<boolean> => {
    if (!selectedDate) return false

    // Validate inputs
    if (!name.trim()) {
      setError('ชื่อรายการค่าใช้จ่ายไม่สามารถเป็นค่าว่างได้')
      return false
    }
    if (amount <= 0) {
      setError('จำนวนเงินต้องมากกว่า 0')
      return false
    }

    setUpdating(true)
    setError(null)

    try {
      const expenseInput: ICreateDailyExpenseInput = {
        dailySummaryId: selectedDate,
        expenseType,
        name: name.trim(),
        amountTHB: amount
      }

      const result = await createExpense(expenseInput)
      
      if (result) {
        // Update local state
        const newExpenseItem: IExpenseItem = {
          id: result.id,
          name: result.name,
          amountTHB: result.amount_thb,
          createdAt: result.created_at,
          updatedAt: result.updated_at
        }

        setExpenses(prev => ({
          ...prev,
          [expenseType]: [...prev[expenseType], newExpenseItem]
        }))
        
        return true
      } else {
        setError('ไม่สามารถเพิ่มรายการค่าใช้จ่ายได้')
        return false
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'เกิดข้อผิดพลาดในการเพิ่มรายการค่าใช้จ่าย')
      console.error('Error adding expense:', err)
      return false
    } finally {
      setUpdating(false)
    }
  }, [selectedDate])

  // Edit expense
  const editExpense = useCallback(async (
    expenseId: string,
    name: string,
    amount: number
  ): Promise<boolean> => {
    // Validate inputs
    if (!name.trim()) {
      setError('ชื่อรายการค่าใช้จ่ายไม่สามารถเป็นค่าว่างได้')
      return false
    }
    if (amount <= 0) {
      setError('จำนวนเงินต้องมากกว่า 0')
      return false
    }

    setUpdating(true)
    setError(null)

    try {
      const updateInput: IUpdateDailyExpenseInput = {
        name: name.trim(),
        amountTHB: amount
      }

      const result = await updateExpense(expenseId, updateInput)
      
      if (result) {
        // Update local state
        setExpenses(prev => {
          const newExpenses = { ...prev }
          
          // Find and update the expense in the appropriate category
          Object.keys(newExpenses).forEach(type => {
            const typeKey = type as keyof IExpensesByType
            const expenseIndex = newExpenses[typeKey].findIndex(exp => exp.id === expenseId)
            
            if (expenseIndex !== -1) {
              newExpenses[typeKey][expenseIndex] = {
                ...newExpenses[typeKey][expenseIndex],
                name: result.name,
                amountTHB: result.amount_thb,
                updatedAt: result.updated_at
              }
            }
          })
          
          return newExpenses
        })
        
        return true
      } else {
        setError('ไม่สามารถแก้ไขรายการค่าใช้จ่ายได้')
        return false
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'เกิดข้อผิดพลาดในการแก้ไขรายการค่าใช้จ่าย')
      console.error('Error editing expense:', err)
      return false
    } finally {
      setUpdating(false)
    }
  }, [])

  // Remove expense
  const removeExpense = useCallback(async (expenseId: string): Promise<boolean> => {
    setUpdating(true)
    setError(null)

    try {
      const result = await deleteExpense(expenseId)
      
      if (result) {
        // Update local state
        setExpenses(prev => {
          const newExpenses = { ...prev }
          
          // Find and remove the expense from the appropriate category
          Object.keys(newExpenses).forEach(type => {
            const typeKey = type as keyof IExpensesByType
            newExpenses[typeKey] = newExpenses[typeKey].filter(exp => exp.id !== expenseId)
          })
          
          return newExpenses
        })
        
        return true
      } else {
        setError('ไม่สามารถลบรายการค่าใช้จ่ายได้')
        return false
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'เกิดข้อผิดพลาดในการลบรายการค่าใช้จ่าย')
      console.error('Error removing expense:', err)
      return false
    } finally {
      setUpdating(false)
    }
  }, [])

  // Calculate total by expense type
  const getTotalByType = useCallback((expenseType: 'FUEL' | 'EMPLOYEE_OVERTIME' | 'OTHER'): number => {
    return expenses[expenseType].reduce((sum, expense) => sum + expense.amountTHB, 0)
  }, [expenses])

  // Calculate grand total
  const getGrandTotal = useCallback((): number => {
    return Object.values(expenses).flat().reduce((sum, expense) => sum + expense.amountTHB, 0)
  }, [expenses])

  // Load expenses when selectedDate changes
  useEffect(() => {
    loadExpenses()
  }, [loadExpenses])

  return {
    // Data
    expenses,
    
    // Loading states
    loading,
    updating,
    
    // Error state
    error,
    
    // Actions
    refreshExpenses,
    addExpense,
    editExpense,
    removeExpense,
    
    // Totals
    getTotalByType,
    getGrandTotal
  }
}